import { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MaterialRequestWrapper, SummaryPanel, SummaryRow, SummaryLabel, SummaryDivider } from '../Styled';
import { BackButtonRow } from '../../../shared/components/BackButton/Styled';
import { IconButton } from '@mui/material';
import BackIcon from '../../../shared/svg/BackIcon';
import {
    BackButtonStyle,
    ButtonRow,
    StyledButton,
    TitleText,
    TitleWrapper,
} from '../../../shared/styled/TitleBar';
import {
    TitleBar,
    WarningCancelMaterialRequest,
} from '../../../configs/materialRequest';
import { SingleMaterialRequest } from '../single';
import useMergeState from '../../../shared/hooks/useMergeState';
import { api } from '../../../shared/hooks/api';
import { error, success } from '../../../utils/toast';
import CancelModal from '../../../shared/components/CancelModal';
import LoadingButton from '../../../shared/components/LoadingButton';
import { WarningUnsavedChanges } from '../../../configs/warningProductModal';

const initialMaterialRequest = {
    due_date: new Date().toISOString().split('T')[0],
    is_urgent: false,
    materials: [],
};

export const CreateMaterialRequest = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);


    const useOtherMaterials = !!((location as any).state?.useOtherMaterials);

    const [materialRequest, mergeMaterialRequest] =
        useMergeState(initialMaterialRequest);
    const [{ isCreating }, createMaterialRequest] =
        api.post('/materials/requests/');
    const [isOpenCancelModal, setIsOpenCancelModal] = useState(false);
    const [isModified, setIsModified] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
    const [navigationPath, setNavigationPath] = useState("");

    // Prefill materials from navigation state or storage (prefer navigation state)
    useEffect(() => {
        // First check location state (for same-tab navigation)
        const state = (location as any).state || {};

        // Handle duplicate
        if (state.duplicateData) {
            const src = state.duplicateData;
            const duplicatedMaterials = (src.materials || []).map((item: any) => ({
                ...item,
                missing_quantity: item.missing_quantity ?? item.request_quantity ?? item.quantity ?? 0,
            }));
            mergeMaterialRequest({
                is_urgent: src.is_urgent,
                due_date: src.due_date,
                note: src.note,
                department: src.department,
                factory_staff: src.factory_staff,
                first_storage_keeper: src.first_storage_keeper,
                second_storage_keeper: src.second_storage_keeper,
                factory_manager: src.factory_manager,
                materials: duplicatedMaterials,
            });
            return;
        }

        let preselectedMaterials = state.preselectedMaterials || [];
        let note = state.note || '';
        let sale_order_ids = state.sale_order_ids || [];
        let requested_products = state.requested_products || [];
        let selected_sale_orders = state.selected_sale_orders || [];

        if (Array.isArray(preselectedMaterials) && preselectedMaterials.length > 0) {
            try { console.log('[MaterialRequest2] Create page prefill', preselectedMaterials); } catch (_) {}
            mergeMaterialRequest({ materials: preselectedMaterials });
        }
        if (note) {
            mergeMaterialRequest({ note });
        }
        if (sale_order_ids) mergeMaterialRequest({ sale_order_ids });
        if (requested_products && Array.isArray(requested_products)) mergeMaterialRequest({ requested_products });
        if (selected_sale_orders && Array.isArray(selected_sale_orders)) mergeMaterialRequest({ selected_sale_orders });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    
    useEffect(() => {
        if (materialRequest.materials.length > 0 || materialRequest.is_urgent) {
            setIsModified(true);
        }
    }, [materialRequest]);

    // Add beforeunload event listener for browser navigation
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (isModified) {
                e.preventDefault();
                e.returnValue = "";
                return "";
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [isModified]);


    const confirmNavigation = useCallback((path: string) => {
        if (isModified) {
            setNavigationPath(path);
            setShowUnsavedDialog(true);
            return false;
        }
        
        navigate(path);
        return true;
    }, [isModified, navigate]);

    const onClickBack = () => {
        confirmNavigation('/material-requests/');
    };

    const handleSaveAndNavigate = async () => {
        setShowUnsavedDialog(false);
        await onClickCreateMaterialRequest();
    };

    const handleDontSaveAndNavigate = () => {
        setShowUnsavedDialog(false);
        navigate(navigationPath);
    };

    const handleContinueEditing = () => {
        setShowUnsavedDialog(false);
    };

    const onClickCreateMaterialRequest = async () => {
        if (isSubmitting || isCreating) return false;

        const materials = materialRequest.materials.map((item: any) => ({
            ...item,
            material_id: item.id,
            quantity: item.missing_quantity,
            provider_name: item.provider_name,
            provider_price: item.provider_price,
        }));


        const requestBody = {
            ...materialRequest,
            materials,
        };

        try {
            setIsSubmitting(true);
            const resp = await createMaterialRequest(requestBody);
            success('THÀNH CÔNG', 'Tạo phiếu lãnh vật tư thành công');
            setIsModified(false);
            navigate(`/material-requests/${resp.id}`);
            return true;
        } catch (err) {
            error('Lỗi tạo phiếu lãnh vật tư', err);
            return false;
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleMaterialOrderRequest = (updates: any) => {
        setIsModified(true);
        mergeMaterialRequest(updates);
    };

    return (
        <MaterialRequestWrapper>
            {isOpenCancelModal && (
                <CancelModal
                    open={isOpenCancelModal}
                    title={WarningCancelMaterialRequest.title}
                    contentText={WarningCancelMaterialRequest.context[0]}
                    handleConfirm={() => onClickBack()}
                    handleClose={() => setIsOpenCancelModal(false)}
                />
            )}

            <CancelModal
                open={showUnsavedDialog}
                handleClose={handleDontSaveAndNavigate}
                handleConfirm={handleSaveAndNavigate}
                handleCancel={handleContinueEditing}
                title={WarningUnsavedChanges.title}
                contentText={WarningUnsavedChanges.context}
                closeButtonText={WarningUnsavedChanges.closeButtonText}
                confirmButtonText={WarningUnsavedChanges.confirmButtonText}
                cancelButtonText={WarningUnsavedChanges.cancelButtonText}
                showCancelButton={true}
            />

            <BackButtonRow>
                <IconButton style={BackButtonStyle} onClick={onClickBack}>
                    <BackIcon />
                </IconButton>
                Trở về
            </BackButtonRow>

            <TitleWrapper>
                <TitleText>{TitleBar.title}</TitleText>
                <ButtonRow>
                    <StyledButton
                        variant="text"
                        onClick={() => setIsOpenCancelModal(true)}
                    >
                        Huỷ bỏ
                    </StyledButton>
                    <LoadingButton
                        variant="contained"
                        text="Tạo yêu cầu"
                        isLoading={isCreating || isSubmitting}
                        disabled={isCreating || isSubmitting}
                        onClick={onClickCreateMaterialRequest}
                    />
                </ButtonRow>
            </TitleWrapper>

            {/* Summary of selected Sale Orders and requested products */}
            {Array.isArray((materialRequest as any).selected_sale_orders) && ((materialRequest as any).selected_sale_orders.length > 0) && (
                <SummaryPanel>
                    <SummaryRow>
                        <SummaryLabel>Đơn hàng:</SummaryLabel>
                        {((materialRequest as any).selected_sale_orders || []).map((o: any, idx: number) => (
                            <>
                                <a key={o.id} href={`/product-orders/${o.id}`} target="_blank" rel="noreferrer">{o.name}</a>
                                {idx < ((materialRequest as any).selected_sale_orders || []).length - 1 ? <SummaryDivider>, </SummaryDivider> : null}
                            </>
                        ))}
                    </SummaryRow>
                    {Array.isArray((materialRequest as any).requested_products) && ((materialRequest as any).requested_products.length > 0) && (
                        <SummaryRow>
                            <SummaryLabel>Sản phẩm yêu cầu:</SummaryLabel>
                            {((materialRequest as any).requested_products || []).map((p: any, idx: number) => (
                                <>
                                    <a key={p.id} href={`/products/${p.id}`} target="_blank" rel="noreferrer">{p.name}</a>
                                    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, backgroundColor: '#f0f2f5', fontWeight: 600, fontSize: 12, lineHeight: '16px', marginLeft: 6 }}>x{p.quantity}</span>
                                    {idx < ((materialRequest as any).requested_products || []).length - 1 ? <SummaryDivider>, </SummaryDivider> : null}
                                </>
                            ))}
                        </SummaryRow>
                    )}
                </SummaryPanel>
            )}

            <SingleMaterialRequest
                materialRequest={materialRequest}
                mergeMaterialRequest={handleMaterialOrderRequest}
                useOtherMaterials={useOtherMaterials}
            />
        </MaterialRequestWrapper>
    );
};
